/**
 * Scoring audit log — stores every RO action in localStorage.
 * Logs are kept for 2 days, then pruned automatically.
 *
 * Storage key: __scoring_log
 * Format: array of { ts, matchId, stageIdx, shooterId, action, data }
 */

const STORAGE_KEY = '__scoring_log';
const MAX_AGE_MS = 2 * 24 * 60 * 60 * 1000; // 2 days

function getLog() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch { return []; }
}

function saveLog(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch { /* quota exceeded — trim oldest half */
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(entries.length / 2)));
    } catch { /* give up */ }
  }
}

function prune() {
  const cutoff = Date.now() - MAX_AGE_MS;
  const entries = getLog().filter((e) => e.ts > cutoff);
  saveLog(entries);
}

/**
 * Log a scoring action.
 * @param {string} action - action name (e.g. 'TARGET_HIT', 'SCORE_SUBMITTED')
 * @param {object} context - { matchId, stageIdx, shooterId }
 * @param {object} [data] - additional payload
 */
export function logAction(action, context = {}, data = {}) {
  prune();
  const entries = getLog();
  entries.push({
    ts: Date.now(),
    matchId: context.matchId || null,
    stageIdx: context.stageIdx || null,
    shooterId: context.shooterId || null,
    action,
    data,
  });
  saveLog(entries);
}

/**
 * Get all log entries, optionally filtered.
 * @param {object} [filter] - { matchId, stageIdx, shooterId }
 * @returns {Array}
 */
export function getLogs(filter = {}) {
  prune();
  let entries = getLog();
  if (filter.matchId) entries = entries.filter((e) => e.matchId === filter.matchId);
  if (filter.stageIdx) entries = entries.filter((e) => e.stageIdx === filter.stageIdx);
  if (filter.shooterId) entries = entries.filter((e) => e.shooterId === filter.shooterId);
  return entries;
}

/**
 * Export logs as a downloadable JSON file.
 * @param {object} [filter] - optional filter
 */
export function exportLogs(filter = {}) {
  const entries = getLogs(filter);
  const blob = new Blob([JSON.stringify(entries, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `scoring-log-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}
